import { db, type MetaRow } from './database';

export const META_KEYS = {
  lastRevision: 'last_revision',
  epoch: 'epoch',
  userId: 'user_id',
  userLogin: 'user_login',
  userRole: 'user_role',
  userTimezone: 'user_timezone',
  userIsAdmin: 'user_is_admin',
} as const;

export async function readMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  if (row === undefined) {
    return fallback;
  }
  return row.value as T;
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  const row: MetaRow = { key, value };
  await db.meta.put(row);
}

export async function deleteMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}

export async function readLastRevision(): Promise<number> {
  const value = await readMeta<number>(META_KEYS.lastRevision, 0);
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
}

export async function writeLastRevision(revision: number): Promise<void> {
  await writeMeta(META_KEYS.lastRevision, Math.max(0, Math.trunc(revision)));
}

export async function readEpoch(): Promise<string> {
  return readMeta<string>(META_KEYS.epoch, '');
}

export async function writeEpoch(epoch: string): Promise<void> {
  await writeMeta(META_KEYS.epoch, epoch);
}

export async function readLocalUserId(): Promise<string | null> {
  const value = await readMeta<string | null>(META_KEYS.userId, null);
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export async function writeLocalUser(user: {
  id: string;
  login: string;
  role: string;
  isAdmin: boolean;
  timezone: string;
}): Promise<void> {
  await writeMeta(META_KEYS.userId, user.id);
  await writeMeta(META_KEYS.userLogin, user.login);
  await writeMeta(META_KEYS.userRole, user.role);
  await writeMeta(META_KEYS.userIsAdmin, user.isAdmin);
  await writeMeta(META_KEYS.userTimezone, user.timezone);
}

export async function clearLocalUser(): Promise<void> {
  await db.meta
    .where('key')
    .anyOf([
      META_KEYS.userId,
      META_KEYS.userLogin,
      META_KEYS.userRole,
      META_KEYS.userIsAdmin,
      META_KEYS.userTimezone,
    ])
    .delete();
}
